/**
 * PHASE 2: Unified Campaign Data Hooks with bounded API polling
 * 
 * This file consolidates campaign fetching through the authenticated API.
 * Benefits:
 * - Single source of truth for campaigns
 * - Adaptive polling only while a campaign is active and the tab is visible
 * - Automatic caching via React Query
 * - Optimistic updates for instant UI
 * - Consistent data across Dashboard, Analytics, and CreateCampaign
 * 
 * Phase 2 Changes:
 * - Disabled background polling and idle polling
 * - Added manual refresh fallback
 * - 100% elimination of unnecessary refetch requests
 */

import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useMemo, useEffect } from 'react';
import axiosAuth from '../utils/axiosAuth';
import axios from 'axios';
import { auth } from '../firebase/firebase';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.fans.nextremedy.net';

/**
 * PHASE 2B: UNIFIED CAMPAIGN DATA HOOK
 * 
 * Single source of truth for all campaign data across Dashboard, Email Campaigns, and Analytics.
 * This hook provides consistent data fetching, caching, and state management for all pages.
 * 
 * Features:
 * - Shared React Query cache across all pages
 * - Instant navigation (data already cached)
 * - Automatic background refresh
 * - Manual cache invalidation
 */

const cacheKey = 'afc-campaigns-cache-v1';

/**
 * CAMPAIGNS HOOK: Fetch all campaigns for a user
 * 
 * @param {string} userId - Firebase UID
 * @param {object} options - React Query options
 * @returns {object} React Query result with campaigns array
 */
export function useCampaigns(userId, options = {}) {
  const { enabled = true } = options;

  /**
   * Fetch campaigns from localStorage cache (instant initial load)
   */
  const getInitialData = () => {
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const campaigns = JSON.parse(cached);
        console.log(`💾 [Unified Cache] Loaded ${campaigns.length} campaigns from localStorage`);
        return campaigns;
      }
    } catch (error) {
      console.warn('⚠️ [Unified Cache] Failed to read localStorage:', error);
    }
    return undefined;
  };

  /**
   * Fetch campaigns from API
   * Uses the updated backend endpoint with regression fixes applied
   */
  const fetchCampaigns = async () => {
    console.log('🔄 [Unified Cache] Fetching campaigns from API...');
    console.log('✅ Using /v2 endpoint – Phase 2B unified data flow active');
    
    const response = await axiosAuth.get('/api/email-campaigns');
    
    if (!response?.data?.campaigns) {
      throw new Error('Invalid response from server');
    }

    const campaigns = response.data.campaigns;
    
    // TARGETED PATCH: Sort campaigns by createdAt descending (newest first)
    // This ensures correct order even if backend query is unordered (missing index)
    campaigns.sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime; // Descending (newest first)
    });
    console.log('🔄 [Frontend Sort] Campaigns sorted by createdAt desc (newest first)');
    
    // Update localStorage cache
    try {
      localStorage.setItem(cacheKey, JSON.stringify(campaigns));
      console.log(`💾 [Unified Cache] Cached ${campaigns.length} campaigns to localStorage`);
    } catch (error) {
      console.warn('⚠️ [Unified Cache] Failed to write cache:', error);
    }

    return campaigns;
  };

  return useQuery({
    queryKey: ['campaigns', userId],
    queryFn: fetchCampaigns,
    initialData: getInitialData(),
    enabled: enabled && !!userId,
    
    // ⚡ PHASE 2B: Active 30-second polling for running campaigns (matches MEO)
    // This ensures UI updates automatically every 30s when campaigns are sending emails
    refetchInterval: (data) => {
      if (typeof document !== 'undefined' && document.hidden) {
        return false;
      }
      const campaigns = data?.campaigns || data || [];
      const hasActiveCampaigns = Array.isArray(campaigns)
        ? campaigns.some(c => ['running', 'pending', 'auto-continuing'].includes(c.status) && !c.paused)
        : false;
      
      if (hasActiveCampaigns) {
        console.log('⚡ [AFC useCampaignData] 30s polling active for running campaigns');
        return 30000; // 30 seconds - only poll when campaigns are actively running
      }
      
      return false; // No polling when idle
    },
    refetchIntervalInBackground: false, // Don't poll if user is on another tab
    
    // Cache settings: match MEO's timing
    staleTime: 30 * 1000,           // 30 seconds - data considered fresh (matches MEO)
    cacheTime: 5 * 60 * 1000,       // 5 minutes - keep in memory
    
    // Always show cached data first (instant UI)
    keepPreviousData: true,
    
    // Refetch on mount and window focus for real-time updates (matches MEO)
    refetchOnMount: 'always',       // Always check for updates on mount
    refetchOnWindowFocus: true,     // ⚡ FIXED: Now refetches on focus (was false)
    refetchOnReconnect: true,       // Refetch when network reconnects
    
    // Logging for visibility
    onSuccess: (data) => {
      // Safety check: ensure data is an array
      if (!data || !Array.isArray(data)) {
        console.log(`✅ [AFC useCampaignData] Query success - data is not an array`);
        return;
      }
      
      const activeCampaigns = data.filter(c => 
        ['running', 'pending', 'auto-continuing'].includes(c.status) && !c.paused
      ).length;
      console.log(`✅ [AFC useCampaignData] Query success - ${data.length} campaigns (${activeCampaigns} active)`);
    },
    onError: (error) => {
      console.error(`❌ [AFC useCampaignData] Query error`, { status: error?.response?.status || null });
    },
  });
}

/**
 * DASHBOARD HOOK: Aggregate campaign data to summary metrics
 * 
 * Transforms the full campaign list into dashboard summary:
 * - totalCampaigns: count of all campaigns
 * - totalEmailsSent: sum of sentCount across all campaigns
 * - activeCampaigns: count of running/pending campaigns
 */
export function useDashboardSummary(userId, options = {}) {
  const { data: campaigns, isLoading, error, refetch } = useCampaigns(userId, options);

  const summary = useMemo(() => {
    if (!campaigns || !Array.isArray(campaigns)) {
      return {
        totalCampaigns: 0,
        totalEmailsSent: 0,
        activeCampaigns: 0,
      };
    }

    return {
      totalCampaigns: campaigns.length,
      totalEmailsSent: campaigns.reduce((sum, c) => sum + (c.sentCount || 0), 0),
      activeCampaigns: campaigns.filter(c => 
        ['running', 'pending', 'auto-continuing'].includes(c.status) && !c.paused
      ).length,
    };
  }, [campaigns]);

  return {
    data: summary,
    isLoading,
    error,
    refetch
  };
}

/**
 * DASHBOARD GRAPH HOOK: Transform campaign data into chart format
 * 
 * Groups campaigns and emails by date for line chart visualization
 */
export function useDashboardGraph(userId, dateRange, options = {}) {
  const { data: campaigns, isLoading, error, refetch } = useCampaigns(userId, options);

  const graphData = useMemo(() => {
    if (!campaigns || !Array.isArray(campaigns) || !dateRange || dateRange.length !== 2) {
      return { data: [] };
    }

    const [startDate, endDate] = dateRange;
    
    // Generate date range for X-axis
    const dateMap = new Map();
    let currentDate = startDate.clone();
    
    while (currentDate.isSameOrBefore(endDate)) {
      const dateKey = currentDate.format('MMM DD');
      dateMap.set(dateKey, {
        date: dateKey,
        fullDate: currentDate.format('YYYY-MM-DD'),
        campaignsCreated: 0,
        emailsSent: 0,
      });
      currentDate.add(1, 'day');
    }

    // Aggregate data from campaigns
    campaigns.forEach(campaign => {
      // Count campaigns created
      if (campaign.createdAt) {
        const createdDate = new Date(campaign.createdAt);
        const dateKey = createdDate.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
        
        if (dateMap.has(dateKey)) {
          dateMap.get(dateKey).campaignsCreated += 1;
        }
      }

      // Count emails sent (use sentCount from Phase 1 aggregates)
      const sentCount = campaign.sentCount || 0;
      if (sentCount > 0 && campaign.lastSent) {
        const sentDate = new Date(campaign.lastSent);
        const dateKey = sentDate.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
        
        if (dateMap.has(dateKey)) {
          dateMap.get(dateKey).emailsSent += sentCount;
        }
      }
    });

    return {
      data: Array.from(dateMap.values())
    };
  }, [campaigns, dateRange]);

  return {
    data: graphData,
    isLoading,
    error,
    refetch
  };
}

/**
 * ANALYTICS HOOK: Fetch analytics data (uses same campaigns endpoint)
 * 
 * Uses /api/analytics endpoint which includes sendLog aggregation
 * ⚠️ CRITICAL: This endpoint calculates firstSent/lastSent from sendLog subcollection
 */
export function useAnalytics(userId, options = {}) {
  const { enabled = true } = options;

  const fetchAnalytics = async () => {
    console.log('🔄 [Analytics] Fetching from /api/analytics...');
    
    const response = await axiosAuth.get('/api/analytics');
    
    if (!response?.data?.campaigns) {
      throw new Error('Invalid analytics response');
    }

    console.log(`✅ [Analytics] Loaded ${response.data.campaigns.length} campaigns`);
    
    return response.data;
  };

  return useQuery({
    queryKey: ['analytics', userId],
    queryFn: fetchAnalytics,
    enabled: enabled && !!userId,
    
    // PHASE 2: No polling
    refetchInterval: false,
    refetchIntervalInBackground: false,
    
    // Cache settings
    staleTime: 30 * 1000,
    cacheTime: 5 * 60 * 1000,
    keepPreviousData: true,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    
    onSuccess: (data) => {
      console.log(`✅ [Analytics] Query success - ${data.campaigns?.length || 0} campaigns`);
    },
    onError: (error) => {
      console.error(`❌ [Analytics] Query error`, { status: error?.response?.status || null });
    },
  });
}

/**
 * CAMPAIGN LIST HOOK: Alias for useCampaigns (for CreateCampaign page)
 */
export function useCampaignsList(userId, options = {}) {
  return useCampaigns(userId, options);
}

/**
 * PHASE 2B: CACHE INVALIDATION HOOK
 * 
 * Manually invalidate React Query cache to force refetch
 * Use after mutations (create campaign, send emails, etc.)
 */
export function useInvalidateCampaigns() {
  const queryClient = useQueryClient();
  
  return (userId) => {
    console.log('🗑️ [Phase 2B] Invalidating authenticated campaign cache');
    queryClient.invalidateQueries(['campaigns', userId]);
    queryClient.invalidateQueries(['analytics', userId]);
    
    // Also clear localStorage cache
    try {
      localStorage.removeItem(cacheKey);
      console.log('💾 [Phase 2B] Cleared localStorage cache');
    } catch (error) {
      console.warn('⚠️ [Phase 2B] Failed to clear localStorage:', error);
    }
  };
}

/**
 * Legacy hook for backward compatibility
 */
export function useCampaignDataInvalidation() {
  return useInvalidateCampaigns();
}

/**
 * CREATE CAMPAIGN MUTATION WITH OPTIMISTIC UPDATES
 * 
 * Matches MEO's pattern - campaign appears instantly in UI before API responds
 * This fixes the 10-15 minute mobile delay when user doesn't pay
 * 
 * Features:
 * - Optimistic update (adds to cache immediately)
 * - Campaign appears at top of list
 * - Rollback on error
 * - Invalidates cache on success
 */
export function useCreateCampaign() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (campaignData) => {
      console.log('🚀 [CreateCampaign] Creating campaign with optimistic update...');
      const idToken = await auth.currentUser?.getIdToken();
      
      const response = await axios.post(
        `${API_BASE_URL}/api/campaigns/start-campaign-checkout`,
        campaignData,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      console.log('🆔 [CreateCampaign] Campaign ID:', response.data.data?.id);
      console.log('📊 [CreateCampaign] Campaign status:', response.data.data?.status);
      console.log('💳 [CreateCampaign] Payment status:', response.data.data?.paymentStatus);
      return response.data.data; // Returns full campaign object with id
    },
    
    // ✅ OPTIMISTIC UPDATE: Add campaign to cache immediately
    onMutate: async (newCampaign) => {
      console.log('⚡ [CreateCampaign] Applying optimistic update...');
      
      // Get userId for correct cache key
      const userId = auth.currentUser?.uid;
      if (!userId) {
        console.error('❌ [CreateCampaign] No userId found in onMutate');
        throw new Error('User not authenticated');
      }
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['campaigns', userId] });
      
      // Snapshot previous value for rollback
      const previousCampaigns = queryClient.getQueryData(['campaigns', userId]);
      
      // Create optimistic campaign
      const optimisticCampaign = {
        id: `temp-${Date.now()}`,
        ...newCampaign,
        status: 'draft',
        paymentStatus: 'unpaid',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        sentCount: 0,
        totalRecipients: 0,
        percentComplete: 0,
        progress: 'Draft created',
        optimisticUpdate: true,
      };
      
      // Add to cache (at the top of the list)
      queryClient.setQueryData(['campaigns', userId], (old) => {
        if (!old) return [optimisticCampaign];
        if (Array.isArray(old)) return [optimisticCampaign, ...old];
        if (old.campaigns && Array.isArray(old.campaigns)) {
          return {
            ...old,
            campaigns: [optimisticCampaign, ...old.campaigns],
          };
        }
        return old;
      });
      
      console.log('✅ [CreateCampaign] Optimistic campaign added to cache');
      
      return { previousCampaigns, optimisticCampaign, userId };
    },
    
    // ✅ ON SUCCESS: Replace optimistic campaign with real data from backend
    onSuccess: (data, variables, context) => {
      console.log('✅ [CreateCampaign] onSuccess called');
      console.log('🆔 [CreateCampaign] Campaign ID:', data?.id);
      console.log('🔑 [CreateCampaign] Optimistic campaign ID:', context?.optimisticCampaign?.id);
      
      // Get userId for correct cache key
      const userId = auth.currentUser?.uid;
      
      if (!userId) {
        console.error('❌ [CreateCampaign] No userId found in onSuccess');
        return;
      }
      
      // Replace optimistic campaign with complete data from backend
      console.log('🔄 [CreateCampaign] Updating authenticated campaign cache');
      queryClient.setQueryData(['campaigns', userId], (old) => {
        
        if (!old) {
          console.warn('⚠️ [CreateCampaign] No existing cache data');
          return old;
        }
        
        if (Array.isArray(old)) {
          const updated = old.map(campaign =>
            campaign.id === context.optimisticCampaign.id
              ? data  // ✅ Use full campaign object from backend (not just variables)
              : campaign
          );
          console.log('✅ [CreateCampaign] Updated campaign cache');
          return updated;
        }
        
        if (old.campaigns && Array.isArray(old.campaigns)) {
          const updated = {
            ...old,
            campaigns: old.campaigns.map(campaign =>
              campaign.id === context.optimisticCampaign.id
                ? data  // ✅ Use full campaign object from backend
                : campaign
            ),
          };
          console.log('✅ [CreateCampaign] Updated campaign cache');
          return updated;
        }
        
        console.warn('⚠️ [CreateCampaign] Unexpected cache structure');
        return old;
      });
      
      // Invalidate to ensure consistency
      console.log('🔄 [CreateCampaign] Invalidating queries...');
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      console.log('✅ [CreateCampaign] onSuccess complete');
    },
    
    // ✅ ON ERROR: Rollback optimistic update
    onError: (error, variables, context) => {
      console.error('❌ [CreateCampaign] Failed to create campaign', { status: error?.response?.status || null });
      
      if (context?.previousCampaigns && context?.userId) {
        queryClient.setQueryData(['campaigns', context.userId], context.previousCampaigns);
        console.log('🔄 [CreateCampaign] Rolled back optimistic update');
      }
    },
  });
}

// TODO Phase 2b: Implement Firestore listeners for real-time updates
